<template>
  <div>
    <mobile-header />
    <side-bar />
    <section class="relative mt-[72px] xl:ml-80 xl:mt-0">
      <game-server-header />
      <div v-if="pendingRestart" class="px-4">
        <div class="mx-auto mt-4 flex w-full max-w-7xl flex-col justify-center gap-4">
          <pending-restart-message :pending-restart="pendingRestart" />
        </div>
      </div>
      <div
        v-if="
          !pendingRestart &&
          showWarning &&
          ((currentVersion !== latestRev && targetVersion === 0 && isRunning) ||
            isModUpdateAvailable ||
            isBungeecord ||
            showDuneRestartHint)
        "
        class="px-4"
      >
        <div class="mx-auto mt-4 flex w-full max-w-container flex-col justify-center gap-4">
          <warning-message
            v-if="isModUpdateAvailable"
            :text="t('ngp/mods/update/warning/text')"
            :title="t('ngp/server/update/warning/title')"
            controls="restart"
          >
          </warning-message>
          <warning-message
            v-else-if="isBungeecord"
            :text="t('ngp/header/bungeecord/warning/text')"
            :title="t('ngp/header/bungeecord/warning/title')"
            controls="bungeecord"
          />
          <warning-message
            v-else-if="showDuneRestartHint"
            :title="t('ngp/header/dune/restarts/title') + battlegroupTimezoneFormatted"
            :soft="true"
          />
          <warning-message
            v-else
            :text="t('ngp/server/update/warning/text')"
            :title="t('ngp/server/update/warning/title')"
            controls="restart"
          ></warning-message>
        </div>
      </div>
      <div
        v-if="activeComponent && !isLoadingLocale"
        data-testid="gameserver-active-component"
        class="px-4 pb-10"
        :style="{ marginBottom: `${contentSpacingBottom}px` }"
      >
        <Component :is="activeComponent" :key="`tab-${activeMenuItem.route}`"></Component>
      </div>
      <interactivity-lock v-if="loading || locked" />
    </section>
  </div>
  <div
    id="action-bar"
    ref="actionBar"
    class="border-gp-cyan bg-gp-midnight-1 z-13 fixed inset-x-0 border-t p-4 shadow-[0_0_10px_rgba(0,0,0,0.3)] empty:hidden xl:left-80"
    :class="isAdmin ? 'bottom-[50px]' : 'bottom-0'"
  ></div>
  <admin-bar />
  <tooltips />
</template>

<script setup lang="ts">
import { type ComputedRef, computed, defineAsyncComponent, markRaw, onMounted, ref, watch, watchEffect } from "vue";

import MobileHeader from "./MobileHeader.vue";
import SideBar from "./SideBar.vue";

import { useRoute, useRouter } from "vue-router";
import { singleRouteParam, useRouteUtils } from "../util/route";
import { useMenu } from "./menu";

import { ensureLocale } from "../i18n";
import { setTitle } from "../util/title";
import AdminBar from "./admin_bar/AdminBar.vue";

import { useGpToasts, useScrollToTop } from "@gportal/uikit";
import { useI18n } from "vue-i18n";
import { usePermissions } from "../util/permissions";
import Tooltips from "./Tooltips.vue";

import { useElementBounding, useWindowFocus } from "@vueuse/core";
import { Service_States } from "../graphql";
import { useClientStore } from "../store/client";
import { useDialogStore } from "../store/dialog";
import { useMaintenanceStore } from "../store/maintenance";
import { CtxLoadError, useServiceStore } from "../store/service";
import { routeLoadTrigger } from "../util/ctxLoadTrigger";
import { truncateText } from "../util/string";
import GameServerHeader from "./header/GameServerHeader.vue";
import InteractivityLock from "./interactivity_lock/InteractivityLock.vue";
import PendingRestartMessage from "./util/PendingRestartMessage.vue";
import WarningMessage from "./util/WarningMessage.vue";
import { DateTime } from "luxon";

const { t } = useI18n();

const router = useRouter();
const route = useRoute();
const { region, locale } = useRouteUtils();

const client = useClientStore();
const service = useServiceStore();
const maintenance = useMaintenanceStore();
const dialog = useDialogStore();

const { activeMenuItem, activeComponent } = useMenu();
const { createToast } = useGpToasts();

const { isAdmin } = usePermissions();
const scrollToTop = useScrollToTop();

const isAuthenticated = computed(() => client.isAuthenticated);
const loading = computed(() => service.isLoading);
const locked = computed(() => service.interactivityLocked);
const hasBattlegroups = computed(() => service.ctx.gameProfile?.hasBattlegroups ?? false);
const battlegroupRestartSchedule = computed(() => service.ctx.profile?.battlegroupRestartSchedule);

const battlegroupTimezoneFormatted = computed(() => {
  if (!battlegroupRestartSchedule.value) {
    return null;
  }

  const [minute, hour] = battlegroupRestartSchedule.value.split(" ");
  if (hour === "*" || minute === "*") {
    return null;
  }

  const dateTime = DateTime.fromObject(
    {
      hour: parseInt(hour, 10),
      minute: parseInt(minute, 10),
    },
    { zone: "utc" },
  );

  return dateTime.toLocal().toFormat("HH:mm ZZZZ");
});
const showDuneRestartHint = computed(() => hasBattlegroups.value && !!battlegroupTimezoneFormatted.value);
const isBungeecord = computed(() => service.ctx.profile?.isConnectedToBungeecord);

const ctxError: ComputedRef<CtxLoadError | undefined> = computed(() => service.ctxLoadError);

const serverTimeInitialised = computed(() => !!client.serverTimeOffset);
const focused = useWindowFocus();

let previousRsid = {};
watchEffect(async () => {
  const currentRsid = service.ctx.service?.config?.rsid;
  if (
    (currentRsid?.id !== previousRsid?.id || currentRsid?.region !== previousRsid?.region) &&
    serverTimeInitialised &&
    currentRsid
  ) {
    console.debug("Subscribing to maintenance lock state");
    await maintenance.subscribeMaintenanceLockState(currentRsid);
    console.debug("Subscribed to maintenance lock state");
    previousRsid = currentRsid;
  }
});

// Re-load profile together with context error and region changes
watch(
  [isAuthenticated, region, ctxError],
  async () => {
    if (!isAuthenticated.value || !region.value) {
      console.debug("Waiting to load profile until we are authenticated and know the region");
      return;
    }
    console.debug("Loading profile");
    try {
      await client.loadMe(region.value);
      console.debug("Profile loaded");
    } catch (e) {
      console.error("Failed to load profile:", e);
    }
  },
  { immediate: true },
);

watch(
  [isAuthenticated, route],
  async ([newAuthState, newRoute], [oldAuthState, oldRoute]) => {
    console.debug(
      `Auth state or route changed, new: ${newAuthState}, ${newRoute.name?.toString()}, old: ${oldAuthState}, ${oldRoute?.name?.toString()}`,
    );
    if (!newAuthState || !newRoute) {
      console.debug("Waiting to load server until we are authenticated and know the route");
      return;
    }
    const authStateChanged = newAuthState != oldAuthState;
    const trigger = routeLoadTrigger({ authStateChanged, ctxLoaded: !!service.ctx.service?.config?.rsid });
    if (newRoute.name == "gameserver") {
      const { serviceId } = newRoute.params;

      // Apparently, when watching multiple parameters, the watch handler will be called on any parameter change
      // regardless of the given ones being unchanged (i.e. on tab change), so we handle this here
      if (
        !authStateChanged &&
        oldRoute &&
        oldRoute.params.region === newRoute.params.region &&
        oldRoute.params.serviceId === serviceId
      ) {
        return;
      }
      console.debug("Loading server by service ID:", serviceId);
      await service.loadAll({ region: region.value, sid: parseInt(singleRouteParam(serviceId)), trigger });
    } else {
      const { gameserverId } = newRoute.params;
      if (
        !authStateChanged &&
        oldRoute &&
        oldRoute.params.region === newRoute.params.region &&
        oldRoute.params.gameserverId === gameserverId
      ) {
        return;
      }
      console.debug("Loading server by gameserver ID:", gameserverId);
      await service.loadAll({
        region: region.value,
        gameserverId: parseInt(singleRouteParam(gameserverId)),
        trigger,
      });
    }
  },
  { immediate: true },
);

// If the server is in the failed FSM state and the tab is focused, stop the server intentionally,
// so we won't keep trying to start it while the user might be fixing it
watchEffect(async () => {
  if (
    focused.value &&
    service.ctx.service?.config?.state == Service_States.Running &&
    service.ctx.service?.currentState?.fsmState == "Failed"
  ) {
    console.debug("Tab is focused and server is in failed state, stopping the server");
    createToast({
      title: t("ngp/notification/stopped_failed_state/title"),
      text: t("ngp/notification/stopped_failed_state/text"),
      timeout: 10000,
    });

    await service.stopService();
  }
});

const isLoadingLocale = ref(false);
const loadGameLocale = async (gameKey?: string, extraGameTranslationKeys?: string[]) => {
  if (gameKey == null) return;

  isLoadingLocale.value = true;
  await ensureLocale(locale.value, gameKey, extraGameTranslationKeys);
  isLoadingLocale.value = false;
};

// Ensure locale on game translation key change
watch(
  () => [service.ctx.sys?.game?.key, service.ctx.sys?.extraGameTranslationKeys],
  ([gameKey, extraGameTranslationKeys]) => {
    loadGameLocale(gameKey as string, extraGameTranslationKeys as string[]);
  },
);

// If we have loaded the game profile (know which one it is)
const gameProfileDetermined = computed(() => !!service.ctx.profile?.name);
// Redirect invalid tabs
watchEffect(() => {
  // We know what tabs can exist once the game profile is loaded/determined
  if (gameProfileDetermined.value && !activeMenuItem.value) {
    // Redirect to status page (no tab segments)
    router.replace({ params: { tabSegments: [] } });
    scrollToTop();
  }
});

// Update the page title
watchEffect(() => {
  const curServerName = service.gameServerState?.name || service.ctx.sys?.gameServer?.serverName;
  if (curServerName) {
    setTitle(truncateText(curServerName, 40));
  }
});

const serviceNamespace = computed(() => service.ctx.service);
const currentState = computed(() => serviceNamespace?.value?.currentState?.state);
const isRunning = computed(() => currentState?.value === Service_States.Running);
const currentVersion = computed(() => service.ctx.service?.config?.currentVersion);
const latestRev = computed(() => service.ctx.service?.latestRev?.id);
const targetVersion = computed(() => service.ctx.service?.config?.targetVersion);
const showWarning = computed(() => service.showWarning);
// A restart waiting for players to leave (TR-5485) replaces the update banner: it is that restart.
const pendingRestart = computed(() => service.ctx.service?.currentState?.pendingRestart ?? null);
const profile = computed(() => service.ctx.profile);
const isModUpdateAvailable = computed(() => profile?.value?.isModUpdateAvailable || false);

// Calculate extra content bottom spacing based on admin flag and action bar
const actionBar = ref<HTMLDivElement>();
const actionBarBounding = useElementBounding(actionBar);

const contentSpacingBottom = computed(() => {
  let value = 0;
  if (isAdmin.value) {
    value += 50;
  }
  value += actionBarBounding.height.value;
  return value;
});

// :)
onMounted(() => {
  const kode = [38, 38, 40, 40, 37, 39, 37, 39, 66, 65];
  const length = kode.length;
  let pos = 0;
  document.addEventListener(
    "keydown",
    function (event) {
      if (event.keyCode === kode[pos++]) {
        if (length === pos) {
          dialog.createDialog({
            component: markRaw(defineAsyncComponent(() => import("./dialogs/DialogEgg.vue"))),
            props: {},
          });
          pos = 0; // ability to start over
          return false;
        }
      } else {
        pos = 0;
      }
    },
    false,
  );
});
</script>
